import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import JusticeScaleLogo from '../assets/images/justice-scale-logo.svg';
import { useGeoLocation } from '../contexts/GeoLocationContext';
import { getFallbackData } from '../utils/billConfig';
import { navigationGroups } from '../utils/navigation';
import { stateNameToSlug } from '../utils/stateUtils';
import BillConcernDialog from './BillConcernDialog';
import DonationDialog from './DonationDialog';
import { trackDarkModeToggle } from './GoogleAnalytics';

type GroupId = (typeof navigationGroups)[number]['id'];

const Header: React.FC<{ toggleTheme: () => void; darkMode: boolean }> = ({ toggleTheme, darkMode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<GroupId[]>([]);
  const [billNamesOpen, setBillNamesOpen] = useState(false);
  const [desktopMenu, setDesktopMenu] = useState<{ id: GroupId; anchor: HTMLElement } | null>(null);
  const [finderOpen, setFinderOpen] = useState(false);
  const pendingFinder = useRef(false);
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const route = useLocation();
  const { location } = useGeoLocation();
  const bills = getFallbackData();

  const closeNavigation = () => {
    setMobileOpen(false);
    setDesktopMenu(null);
  };

  useEffect(() => {
    setMobileOpen(false);
    setDesktopMenu(null);
    setCollapsedGroups([]);
    setBillNamesOpen(false);
  }, [route.pathname, route.search, desktop]);

  const getBillUrl = (billRoute: string) =>
    location?.state ? `/bill/${stateNameToSlug(location.state)}/${billRoute}` : `/bill/${billRoute}`;

  const isCurrent = (to: string) => route.pathname === to;
  const isGroupCurrent = (id: GroupId) =>
    id === 'bills'
      ? route.pathname.startsWith('/bill/') || isCurrent('/family-court-reform-bills')
      : navigationGroups.find((group) => group.id === id)?.links.some((link) => isCurrent(link.to));

  const openFinder = () => {
    // Let the navigation restore focus and finish exiting before opening another modal.
    pendingFinder.current = true;
    closeNavigation();
  };
  const onNavigationExited = () => {
    setBillNamesOpen(false);
    if (pendingFinder.current) {
      pendingFinder.current = false;
      setFinderOpen(true);
    }
  };

  const controlStyles = {
    minHeight: 48,
    borderRadius: 1.5,
    color: 'text.primary',
    textTransform: 'none' as const,
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.5,
    '&:hover': { bgcolor: 'action.hover' },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: '2px solid',
      outlineColor: 'text.primary',
      outlineOffset: 2,
    },
  };
  const mobileRowStyles = {
    ...controlStyles,
    width: '100%',
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
    textAlign: 'left' as const,
  };
  const finderStyles = {
    ...mobileRowStyles,
    gap: 1.5,
    py: 2,
    border: '1px solid',
    borderColor: (currentTheme: typeof theme) => alpha(currentTheme.palette.primary.main, 0.5),
    bgcolor: (currentTheme: typeof theme) => alpha(currentTheme.palette.primary.main, 0.09),
    '&:hover': { bgcolor: (currentTheme: typeof theme) => alpha(currentTheme.palette.primary.main, 0.16) },
  };

  const finderContent = (
    <>
      <SearchIcon aria-hidden="true" sx={{ flexShrink: 0 }} />
      <Box component="span" sx={{ flex: 1 }}>
        <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
          Find a bill for your concern
        </Box>
        <Box
          component="span"
          sx={{ display: 'block', mt: 0.5, fontSize: '0.8125rem', fontWeight: 400, color: 'text.secondary' }}
        >
          Start with what matters to your family.
        </Box>
      </Box>
      <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18, flexShrink: 0 }} />
    </>
  );

  return (
    <Box
      component="header"
      sx={{
        bgcolor: 'background.paper',
        '& a:focus-visible, & button:focus-visible': {
          outline: '2px solid',
          outlineColor: 'text.primary',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="a"
        href="#page-content"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 1500,
          transform: 'translateY(-200%)',
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 1,
          fontWeight: 700,
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        Skip to content
      </Box>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 3, lg: 0 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, lg: 88 }, gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
            <Box
              component={Link}
              to="/"
              aria-label="RobertGarza.us home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1.25 },
                color: 'inherit',
                textDecoration: 'none',
                mr: { xs: 'auto', lg: 0 },
                minWidth: 0,
                flexShrink: 0,
                maxWidth: '100%',
                minHeight: 48,
              }}
            >
              <Box
                component="img"
                src={JusticeScaleLogo}
                alt=""
                sx={{
                  height: { xs: 24, sm: 32 },
                  width: { xs: 24, sm: 32 },
                  flexShrink: 0,
                  filter: darkMode
                    ? 'invert(73%) sepia(49%) saturate(765%) hue-rotate(6deg) brightness(101%) contrast(104%)'
                    : 'invert(32%) sepia(91%) saturate(831%) hue-rotate(28deg) brightness(91%) contrast(92%)',
                }}
              />
              <Box>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontFamily: 'Georgia, serif',
                    fontWeight: 700,
                    fontSize: { xs: '0.9375rem', sm: '1.125rem' },
                    lineHeight: 1.3,
                  }}
                >
                  RobertGarza.us
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    fontSize: '0.6875rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mt: 0,
                  }}
                >
                  Family court reform
                </Typography>
              </Box>
            </Box>

            <Box
              component="nav"
              aria-label="Main navigation"
              sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.5, flexWrap: 'wrap', mr: 'auto' }}
            >
              {navigationGroups.map((group) => (
                <Button
                  key={group.id}
                  id={`header-${group.id}-trigger`}
                  aria-haspopup="menu"
                  aria-expanded={desktopMenu?.id === group.id}
                  aria-controls={desktopMenu?.id === group.id ? `header-${group.id}-menu` : undefined}
                  onClick={(event) => setDesktopMenu({ id: group.id, anchor: event.currentTarget })}
                  endIcon={
                    <ExpandMoreIcon sx={{ transform: desktopMenu?.id === group.id ? 'rotate(180deg)' : 'none' }} />
                  }
                  sx={{ ...controlStyles, px: 3, bgcolor: isGroupCurrent(group.id) ? 'action.selected' : undefined }}
                >
                  {group.label}
                </Button>
              ))}
              <Button
                component={Link}
                to="/blog"
                aria-current={isCurrent('/blog') ? 'page' : undefined}
                sx={{ ...controlStyles, px: 3 }}
              >
                Articles
              </Button>
            </Box>
            <IconButton
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={() => {
                toggleTheme();
                trackDarkModeToggle(darkMode ? 'light' : 'dark');
              }}
              sx={{ ...controlStyles, display: { xs: 'none', lg: 'inline-flex' }, width: 48 }}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                ml: { xs: 'auto', lg: 0 },
                flexShrink: 0,
                '& > div > .MuiButton-root': {
                  ...controlStyles,
                  px: { xs: 1, sm: 2 },
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .MuiSvgIcon-root': { display: 'inline-block', fontSize: 20, color: 'primary.main' },
                },
              }}
            >
              <DonationDialog />
              <IconButton
                aria-label="Open navigation"
                aria-haspopup="dialog"
                aria-expanded={mobileOpen}
                aria-controls={mobileOpen ? 'mobile-navigation' : undefined}
                onClick={() => setMobileOpen(true)}
                sx={{ ...controlStyles, width: 48, display: { lg: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {navigationGroups.map((group) => (
        <Menu
          key={group.id}
          anchorEl={desktopMenu?.id === group.id ? desktopMenu.anchor : null}
          open={desktopMenu?.id === group.id}
          onClose={() => setDesktopMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          TransitionProps={{ onExited: onNavigationExited }}
          MenuListProps={{ id: `header-${group.id}-menu`, 'aria-labelledby': `header-${group.id}-trigger` }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              width: group.id === 'bills' ? 440 : 300,
              maxHeight: 'calc(100dvh - 112px)',
              maxWidth: 'calc(100vw - 32px)',
              '& .MuiMenuItem-root': { ...controlStyles, whiteSpace: 'normal', px: 2, py: 1.25 },
              '& .MuiMenuItem-root[aria-current="page"]': { bgcolor: 'action.selected' },
            },
          }}
        >
          {group.id === 'bills'
            ? [
                <MenuItem key="finder" onClick={openFinder} sx={finderStyles}>
                  {finderContent}
                </MenuItem>,
                <MenuItem
                  key="all"
                  component={Link}
                  to="/family-court-reform-bills"
                  onClick={closeNavigation}
                  aria-current={isCurrent('/family-court-reform-bills') ? 'page' : undefined}
                  sx={{ justifyContent: 'space-between', mt: 0.5 }}
                >
                  Browse all bills <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                </MenuItem>,
                <Divider key="divider" sx={{ my: 1 }} />,
                <MenuItem
                  key="names"
                  aria-expanded={billNamesOpen}
                  onClick={() => setBillNamesOpen(!billNamesOpen)}
                  sx={{ justifyContent: 'space-between' }}
                >
                  Browse bills by name ({bills.length})
                  <ExpandMoreIcon aria-hidden="true" sx={{ transform: billNamesOpen ? 'rotate(180deg)' : 'none' }} />
                </MenuItem>,
                ...(billNamesOpen ? bills : []).map((bill) => (
                  <MenuItem
                    key={bill.route}
                    component={Link}
                    to={getBillUrl(bill.route)}
                    onClick={closeNavigation}
                    aria-current={isCurrent(getBillUrl(bill.route)) ? 'page' : undefined}
                  >
                    {bill.pageTitle}
                  </MenuItem>
                )),
              ]
            : group.links.map((link) => (
                <MenuItem
                  key={link.to}
                  component={Link}
                  to={link.to}
                  onClick={closeNavigation}
                  aria-current={isCurrent(link.to) ? 'page' : undefined}
                >
                  {link.label}
                </MenuItem>
              ))}
        </Menu>
      ))}

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeNavigation}
        SlideProps={{
          onExited: () => {
            setCollapsedGroups([]);
            setBillNamesOpen(false);
            onNavigationExited();
          },
        }}
        PaperProps={{
          id: 'mobile-navigation',
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'mobile-navigation-title',
          sx: {
            width: { xs: '100%', sm: 400 },
            maxWidth: '100%',
            height: '100%',
            pt: 'env(safe-area-inset-top)',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography
            id="mobile-navigation-title"
            sx={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem', fontWeight: 700 }}
          >
            Explore the mission
          </Typography>
          <IconButton onClick={closeNavigation} aria-label="Close navigation" sx={{ ...controlStyles, width: 48 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="nav" aria-label="Mobile navigation" sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto', flex: 1 }}>
          <Button
            component={Link}
            to="/"
            onClick={closeNavigation}
            aria-current={isCurrent('/') ? 'page' : undefined}
            sx={mobileRowStyles}
          >
            Home
          </Button>
          {navigationGroups.map((group) => (
            <Box key={group.id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Button
                id={`mobile-${group.id}-trigger`}
                aria-expanded={!collapsedGroups.includes(group.id)}
                aria-controls={`mobile-${group.id}-links`}
                onClick={() =>
                  setCollapsedGroups((groups) =>
                    groups.includes(group.id) ? groups.filter((id) => id !== group.id) : [...groups, group.id],
                  )
                }
                endIcon={
                  <ExpandMoreIcon sx={{ transform: !collapsedGroups.includes(group.id) ? 'rotate(180deg)' : 'none' }} />
                }
                sx={{
                  ...mobileRowStyles,
                  fontSize: '1.125rem',
                  bgcolor: isGroupCurrent(group.id) ? 'action.selected' : undefined,
                }}
              >
                {group.label}
              </Button>
              <Collapse in={!collapsedGroups.includes(group.id)} unmountOnExit>
                <Box
                  id={`mobile-${group.id}-links`}
                  role="region"
                  aria-labelledby={`mobile-${group.id}-trigger`}
                  sx={{ py: 1, pl: 1 }}
                >
                  {group.id === 'bills' ? (
                    <>
                      <Button onClick={openFinder} sx={finderStyles}>
                        {finderContent}
                      </Button>
                      <Button
                        component={Link}
                        to="/family-court-reform-bills"
                        onClick={closeNavigation}
                        sx={{ ...mobileRowStyles, mt: 1 }}
                      >
                        Browse all bills <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                      </Button>
                      <Button
                        aria-expanded={billNamesOpen}
                        aria-controls="mobile-bill-names"
                        onClick={() => setBillNamesOpen(!billNamesOpen)}
                        sx={mobileRowStyles}
                        endIcon={<ExpandMoreIcon sx={{ transform: billNamesOpen ? 'rotate(180deg)' : 'none' }} />}
                      >
                        Browse bills by name ({bills.length})
                      </Button>
                      <Collapse in={billNamesOpen} unmountOnExit>
                        <Box id="mobile-bill-names" sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 2 }}>
                          {bills.map((bill) => (
                            <Button
                              key={bill.route}
                              component={Link}
                              to={getBillUrl(bill.route)}
                              onClick={closeNavigation}
                              aria-current={isCurrent(getBillUrl(bill.route)) ? 'page' : undefined}
                              sx={mobileRowStyles}
                            >
                              {bill.pageTitle}
                            </Button>
                          ))}
                        </Box>
                      </Collapse>
                    </>
                  ) : (
                    group.links.map((link) => (
                      <Button
                        key={link.to}
                        component={Link}
                        to={link.to}
                        onClick={closeNavigation}
                        aria-current={isCurrent(link.to) ? 'page' : undefined}
                        sx={mobileRowStyles}
                      >
                        {link.label}
                      </Button>
                    ))
                  )}
                </Box>
              </Collapse>
            </Box>
          ))}
          <Button
            component={Link}
            to="/blog"
            onClick={closeNavigation}
            aria-current={isCurrent('/blog') ? 'page' : undefined}
            sx={mobileRowStyles}
          >
            Articles
          </Button>
        </Box>
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Button
            onClick={() => {
              toggleTheme();
              trackDarkModeToggle(darkMode ? 'light' : 'dark');
            }}
            startIcon={darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            sx={{ ...controlStyles, px: 2 }}
          >
            {darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          </Button>
        </Box>
      </Drawer>
      <BillConcernDialog open={finderOpen} onClose={() => setFinderOpen(false)} />
    </Box>
  );
};

export default Header;
